import { motion } from 'motion/react';
import { FaCheck } from 'react-icons/fa';

import { AvatarImage } from './AvatarImage';
import { Professional } from '@aldebaran/prisma';

export interface AnimatedProfessionalAvatarProps {
  professional?: Pick<
    Professional,
    'firstname' | 'lastname' | 'avatarId' | 'title' | 'gender'
  >;
  size?: number;
}

function AvatarMask({ size }: { size: number }) {
  const padding = 5;
  const rad = 24;
  const l = padding;
  const r = size - padding;
  const t = padding;
  const b = size - padding;
  // Path starts at bottom-right so the stroke animation begins there
  const pathD = `M ${r - rad},${b} L ${l + rad},${b} Q ${l},${b} ${l},${
    b - rad
  } L ${l},${t + rad} Q ${l},${t} ${l + rad},${t} L ${
    r - rad
  },${t} Q ${r},${t} ${r},${t + rad} L ${r},${b - rad} Q ${r},${b} ${
    r - rad
  },${b} Z`;

  return (
    <svg
      className="absolute inset-0 block size-full pointer-events-none"
      fill="none"
      preserveAspectRatio="none"
      viewBox={`0 0 ${size} ${size}`}
    >
      <motion.path
        d={pathD}
        stroke="#5ECA1A"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        initial={{ pathLength: 0 }}
        animate={{
          pathLength: 1,
        }}
        transition={{
          duration: 1.2,
          delay: 0.5,
          ease: 'easeOut',
        }}
      />
    </svg>
  );
}

function ValidationCheckmark({ size }: { size: number }) {
  const checkmarkSize = size * 0.35; // ~35% of avatar size
  const checkmarkPositionX = size * 0.8; // Horizontal position
  const checkmarkPositionY = size * 0.8; // Vertical position

  return (
    <motion.div
      className="absolute z-10"
      style={{
        width: `${checkmarkSize}px`,
        height: `${checkmarkSize}px`,
        left: `${checkmarkPositionX}px`,
        top: `${checkmarkPositionY}px`,
      }}
      initial={{ scale: 0, rotate: -180 }}
      animate={{
        scale: [0, 1.3, 1],
        rotate: 0,
      }}
      transition={{
        duration: 0.6,
        delay: 1.5,
        ease: [0.34, 1.56, 0.64, 1],
      }}
    >
      <div className="relative w-full h-full">
        {/* Glow effect - behind the icon, stays at max */}
        <motion.div
          className="absolute inset-0 rounded-full bg-[#5ECA1A] blur-md -z-10"
          initial={{ scale: 0, opacity: 0 }}
          animate={{
            scale: 1.3,
            opacity: 0.4,
          }}
          transition={{
            duration: 0.8,
            delay: 1.5,
            ease: 'easeOut',
          }}
        />

        {/* Icon itself - no blur, on top */}
        <div className="absolute inset-0 bg-[#5ECA1A] rounded-full flex items-center justify-center z-10">
          <FaCheck
            className="text-white"
            style={{ width: '60%', height: '60%' }}
          />
        </div>
      </div>
    </motion.div>
  );
}

export const AnimatedProfessionalAvatar = ({
  professional,
  size = 160,
}: AnimatedProfessionalAvatarProps) => {
  const alt = `${professional?.title ?? ''} ${professional?.firstname ?? ''} ${
    professional?.lastname ?? ''
  }`.trim();

  return (
    <motion.div
      className="relative"
      style={{
        width: `${size}px`,
        height: `${size}px`,
      }}
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      {/* Avatar Container: padding aligns with green stroke, white layer adds gap to photo */}
      <div className="absolute inset-0 p-[5px]">
        <div className="h-full w-full rounded-3xl bg-white p-2">
          <div
            className="relative h-full w-full overflow-hidden rounded-2xl z-10"
            role="img"
            aria-label={alt || undefined}
          >
            <AvatarImage
              avatarId={professional?.avatarId}
              gender={professional?.gender}
              alt={alt}
            />
          </div>
        </div>
      </div>

      {/* Animated Circle Mask */}
      <AvatarMask size={size} />

      {/* Validation Checkmark with animations */}
      <ValidationCheckmark size={size} />
    </motion.div>
  );
};
